import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from "@angular/common";
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { ModuleWithProviders, NgModule } from "@angular/core";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatAutocompleteModule } from "@angular/material/autocomplete";
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from "@angular/material/button";
import { MatButtonToggleModule } from "@angular/material/button-toggle";
import { MatCardModule } from "@angular/material/card";
import { MatCheckboxModule } from "@angular/material/checkbox";
import { MatChipsModule } from "@angular/material/chips";
import { MatNativeDateModule, MatRippleModule } from "@angular/material/core";
import { MatDatepickerModule } from "@angular/material/datepicker";
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatExpansionModule } from "@angular/material/expansion";
import { MatGridListModule } from "@angular/material/grid-list";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { MatListModule } from "@angular/material/list";
import { MatMenuModule } from "@angular/material/menu";
import { MatPaginatorModule } from '@angular/material/paginator';
import { MatProgressBarModule } from "@angular/material/progress-bar";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { MatRadioModule } from "@angular/material/radio";
import { MatSelectModule } from "@angular/material/select";
import { MatSidenavModule } from "@angular/material/sidenav";
import { MatSlideToggleModule } from "@angular/material/slide-toggle";
import { MatSliderModule } from "@angular/material/slider";
import { MatSnackBarModule } from "@angular/material/snack-bar";
import { MatSortModule } from '@angular/material/sort';
import { MatStepperModule } from "@angular/material/stepper";
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from "@angular/material/tabs";
import { MatToolbarModule } from "@angular/material/toolbar";
import { MatTooltipModule } from "@angular/material/tooltip";
import { TranslateModule } from '@ngx-translate/core';
import { FooterComponent } from "app/admin/layout/footer/footer.component";
import { CookieService } from 'ngx-cookie-service';
import { DragScrollModule } from "ngx-drag-scroll";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";
import { NgxUploaderModule } from 'ngx-uploader';
import { ConfirmDialogComponent } from "../../shared/components/confirm-dialog/confirm-dialog.component";
import { EmployeeCommonAttachmentDetailsComponent } from "../../shared/components/employees/attachments/edit/employee-attachment-details.component";
import { EmployeeCommonAttachmentsComponent } from "../../shared/components/employees/attachments/list/employee-attachments.component";
import { EmployeeDetailsPanelComponent } from "../../shared/components/employees/employee-details-panel/employee-details-panel.component";
import { AutofocusDirective } from '../../shared/directives/autofocus.directive';
import { LoginService } from '../../shared/pages/login/login.service';
import { NumberArrayPipe } from "../../shared/pipes/number-array.pipe";
import { AuthGuard } from '../../shared/services/authentication/guard.service';
import { JwtHttpInterceptor } from '../../shared/services/authentication/interceptor.service';
import { GlobalService } from "../../shared/services/global/global.service";
import { GraphService } from "../../shared/services/graph/graph.service";
import { PCodeService } from '../../shared/services/pcode/pcode.service';
import { DashboardVehicleMileageReadingDialogComponent } from "../pages/dashboard/widgets/companyvehicle/dashboard-vehicle-mileage-reading-dialog/dashboard-vehicle-mileage-reading-dialog.component";
import { FileManagerFolderDetailsDialogComponent } from '../pages/filemanager/filemanager/folder-details-dialog/folder-details-dialog.component';
import { FileManagerRenameDialogComponent } from '../pages/filemanager/filemanager/rename-dialog/rename-dialog.component';
import { ChildFlowComponent } from "../pages/flows/flows/child-flow/child-flow.component";
import { FlowAttachmentsComponent } from "../pages/flows/flows/flow-attachments/flow-attachments.component";
import { MandatoryFieldsComponent } from "./employees/employees/mandatory-fields/mandatory-fields.component";
import { FlowTreeViewDetailsComponent } from "../pages/flows/flows/tree-view/tree-view-details/tree-view-details.component";
import { FlowTreeViewTemplateComponent } from "../pages/flows/flows/tree-view/tree-view-template/tree-view-template.component";
import { AssignTaskDialogComponent } from "../pages/flows/tasks/assign-dialog/assign-dialog.component";
import { CancelTaskDialogComponent } from "../pages/flows/tasks/cancel-dialog/cancel-dialog.component";
import { TaskAttachmentsComponent } from "../pages/flows/tasks/task-attachments/task-attachments.component";
import { UploadVisibleAttachmentDialogComponent } from "../pages/profile/employeedocs/upload-visible-attachments/upload-visible-attachments.component";
import { CheckinoutModalComponent } from "../pages/vehicles/rent-requests/checkinout-modal/checkinout-modal.component";
import { AppBackdropComponent } from "./components/app_backdrop/app_backdrop.component";
import { SmdFabSpeedDialActionsComponent, SmdFabSpeedDialComponent, SmdFabSpeedDialTriggerComponent } from "./components/fab";
import { ForgotPasswordDialogComponent } from './components/forgot-password/forgot-password-dialog.component';
import { NotificationDetailsDialogComponent } from './components/notifications/notification-details-dialog/notification-details-dialog.component';
import { SendNotificationDialogComponent } from './components/notifications/send-notification-dialog/send-notification-dialog.component';
import { ResetPasswordInternalComponent } from './components/reset-password/reset-password.component';
import { TaskComponent } from './components/todo/task.component';
import { TodoComponent } from './components/todo/todo.component';
import { UploadDialogComponent } from './components/upload/upload.component';
import { WeatherComponent } from "./components/weather/weather.component";
import { UploadDeadlineActivityFilesDialogComponent } from "./deadlines/company-deadlines/upload-deadline-activity-files/upload-deadline-activity-files.component";
import { UploadDeadlineFilesDialogComponent } from "./deadlines/company-deadlines/upload-deadline-files/upload-deadline-files.component";
import { PersonInfoComponent } from "./employees/employees/person-info/person-info.component";
import { PersonTooltipDirective } from "./employees/employees/person-tooltip/person-tooltip.directive";
import { EmployeeSelectorDialogComponent } from "./employees/employees/selector/selector.component";
import { UploadAttachmentsDialogComponent } from "./employees/employees/upload-attachments/upload-attachments.component";
import { MultipleUsersSelectorDialogComponent } from "./users/users/multiple-selector/selector.component";
import { UserSelectorDialogComponent } from "./users/users/selector/selector.component";
import { SoftwareSelectorDialogComponent } from "./softwarelicenses/managementgroups/selector/selector.component";
import { DatePipe } from '@angular/common';
import { UploadTemplateCircularDialogComponent } from '../pages/circulars/templates/upload-template-circular/upload-template-circular.component';
import { UploadCircularDialogComponent } from './circulars/upload-circular/upload-circular.component';
import { EmployeeOutOfTownAlertPanelComponent } from '../../shared/components/employees/employee-out-of-town-alert/employee-out-of-town-alert.component';
import { ImageDialogComponent } from '../../shared/components/image-dialog/image-dialog.component';
import { MultipleCostCentersSelectorDialogComponent } from './costcenters/costcenters/multiple-selector/selector.component';
import { GuestSelectorDialogComponent } from "./guests/guests/selector/selector.component";
import { ChargingStationParkingMapComponent } from '../pages/chargingstation/parking-map/parking-map.component';
import { UploadGuestDocumentComponent } from './events/upload-guest-document/upload-guest-document.component';
import { UploadSupplierDocumentComponent } from './registries/upload-supplier-document/upload-supplier-document.component';
import { CoursesDashboardComponent } from './courses/dashboard/dashboard.component';
import { AssetSelectorDialogComponent } from './assets/selector/asset-selector.component';
import { EmployeeAvatarComponent } from './employees/employees/employee-avatar/employee-avatar.component';

@NgModule({
	declarations:
		[
			// components
			AppBackdropComponent,
			FooterComponent,
			TaskComponent,
			WeatherComponent,
			TodoComponent,
			SmdFabSpeedDialActionsComponent,
			SmdFabSpeedDialComponent,
			SmdFabSpeedDialTriggerComponent,
			ResetPasswordInternalComponent,

			// dialogs
			UploadDialogComponent,
			FileManagerFolderDetailsDialogComponent,
			FileManagerRenameDialogComponent,
			UserSelectorDialogComponent,
			SendNotificationDialogComponent,
			NotificationDetailsDialogComponent,
			ForgotPasswordDialogComponent,
			UploadAttachmentsDialogComponent,
			UploadDeadlineActivityFilesDialogComponent,
			UploadDeadlineFilesDialogComponent,
			UploadVisibleAttachmentDialogComponent,
			UploadCircularDialogComponent,
			UploadGuestDocumentComponent,
			UploadSupplierDocumentComponent,
			UploadTemplateCircularDialogComponent,
			ConfirmDialogComponent,
			ImageDialogComponent,

			// custom components
			AssignTaskDialogComponent,
			CancelTaskDialogComponent,
			EmployeeSelectorDialogComponent,
			MultipleUsersSelectorDialogComponent,
			MultipleCostCentersSelectorDialogComponent,
			FlowAttachmentsComponent,
			TaskAttachmentsComponent,
			ChildFlowComponent,
			MandatoryFieldsComponent,
			FlowTreeViewTemplateComponent,
			FlowTreeViewDetailsComponent,
			PersonInfoComponent,
			EmployeeAvatarComponent,
			DashboardVehicleMileageReadingDialogComponent,
			CheckinoutModalComponent,
			EmployeeCommonAttachmentsComponent,
			EmployeeCommonAttachmentDetailsComponent,
			EmployeeDetailsPanelComponent,
			EmployeeOutOfTownAlertPanelComponent,
			SoftwareSelectorDialogComponent,
			GuestSelectorDialogComponent,
			ChargingStationParkingMapComponent,
			CoursesDashboardComponent,
			AssetSelectorDialogComponent,

			// directives
			AutofocusDirective,
			PersonTooltipDirective,
			NumberArrayPipe
		],
	imports:
		[
			// commons
			CommonModule,
			FormsModule,
			ReactiveFormsModule,

			// material
			MatAutocompleteModule,
			MatButtonModule,
			MatButtonToggleModule,
			MatCardModule,
			MatCheckboxModule,
			MatChipsModule,
			MatDatepickerModule,
			MatDialogModule,
			MatExpansionModule,
			MatGridListModule,
			MatIconModule,
			MatInputModule,
			MatListModule,
			MatMenuModule,
			MatNativeDateModule,
			MatProgressBarModule,
			MatProgressSpinnerModule,
			MatRadioModule,
			MatRippleModule,
			MatSelectModule,
			MatSidenavModule,
			MatSliderModule,
			MatSlideToggleModule,
			MatSnackBarModule,
			MatTabsModule,
			MatStepperModule,
			MatToolbarModule,
			MatTooltipModule,
			MatBadgeModule,
			DragDropModule,

			TranslateModule,
			NgxUploaderModule,
			NgxMatSelectSearchModule,

			// tables
			MatTableModule,
			MatSortModule,
			MatPaginatorModule,

			DragScrollModule
		],
	exports:
		[
			// commons
			CommonModule,
			FormsModule,

			// material
			MatAutocompleteModule,
			MatButtonModule,
			MatButtonToggleModule,
			MatCardModule,
			MatCheckboxModule,
			MatChipsModule,
			MatDatepickerModule,
			MatDialogModule,
			MatExpansionModule,
			MatGridListModule,
			MatIconModule,
			MatInputModule,
			MatListModule,
			MatMenuModule,
			MatNativeDateModule,
			MatProgressBarModule,
			MatProgressSpinnerModule,
			MatRadioModule,
			MatRippleModule,
			MatSelectModule,
			MatSidenavModule,
			MatSliderModule,
			MatSlideToggleModule,
			MatSnackBarModule,
			MatTabsModule,
			MatStepperModule,
			MatToolbarModule,
			MatTooltipModule,
			MatBadgeModule,
			DragDropModule,

			// tables
			MatTableModule,
			MatSortModule,
			MatPaginatorModule,

			DragScrollModule,

			SmdFabSpeedDialActionsComponent,
			SmdFabSpeedDialComponent,
			SmdFabSpeedDialTriggerComponent,
			AppBackdropComponent,
			FooterComponent,
			TaskComponent,
			WeatherComponent,
			TodoComponent,
			ReactiveFormsModule,
			TranslateModule,
			NgxUploaderModule,
			NgxMatSelectSearchModule,

			// components
			ResetPasswordInternalComponent,
			FlowAttachmentsComponent,
			TaskAttachmentsComponent,
			ChildFlowComponent,
			MandatoryFieldsComponent,
			FlowTreeViewTemplateComponent,
			FlowTreeViewDetailsComponent,
			PersonInfoComponent,
			EmployeeAvatarComponent,
			DashboardVehicleMileageReadingDialogComponent,
			CheckinoutModalComponent,
			EmployeeCommonAttachmentsComponent,
			EmployeeCommonAttachmentDetailsComponent,
			EmployeeDetailsPanelComponent,
			EmployeeOutOfTownAlertPanelComponent,
			ChargingStationParkingMapComponent,
			CoursesDashboardComponent,

			// directives
			AutofocusDirective,
			PersonTooltipDirective,
			NumberArrayPipe
		],
	providers:
		[
			// custom services
			AuthGuard,
			LoginService,
			CookieService,
			PCodeService,
			GlobalService,
			GraphService,
			DatePipe,
			{ provide: MAT_DIALOG_DATA, useValue: {} },
			{ provide: HTTP_INTERCEPTORS, useClass: JwtHttpInterceptor, multi: true },
			{ provide: MatDialogRef, useValue: {} }
		]
})
export class AdminSharedModule {
	static forRoot(): ModuleWithProviders<AdminSharedModule> {
		return {
			ngModule: AdminSharedModule
		};
	}
}
